import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import { jobRolesRoutes } from 'constants/navigation';
import { formatJobMetadata, specialJobFamilyLabels } from 'constants/roleLevel';
import {
  ProjectJobRoleFindManyDocument,
  ProjectJobRoleFindManyQuery,
  ProjectJobRoleFindManyQueryVariables,
  ProjectJobRoleFragment,
  ProjectStatus,
  useProjectJobRoleSetMainMutation,
} from 'generated/graphql';
import { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  RemoveProjectJobRoleButton,
  SetMainRoleButton,
} from 'views/Project/components/ProjectJobRolesOverviewPresentational/ProjectJobRolesButtons';
import {
  Button,
  Tag,
  TagColor,
  TagSize,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ProjectJobRoleRow.module.scss';

type TProjectJobRoleRowProps = {
  projectJobRole: ProjectJobRoleFragment;
  projectId: number;
  projectStatus: ProjectStatus;
  isEnabledProjectAction: boolean;
  isMultiMatchProject: boolean;
};

const ProjectJobRoleRow: FC<TProjectJobRoleRowProps> = ({
  projectJobRole: {
    id,
    jobRole: { name, id: jobRoleId, roleLevel, family },
    skillsProfileId,
    isMainRole,
  },
  projectId,
  projectStatus,
  isEnabledProjectAction,
  isMultiMatchProject,
}) => {
  const roleLevelLabel = formatJobMetadata(roleLevel);
  const jobFamilyLabel = family
    ? specialJobFamilyLabels[family] ?? formatJobMetadata(family)
    : '';

  const { handleMsgType } = useNotification();
  const location = useLocation();

  const jobRoleRoute = jobRolesRoutes.editJobRole.url(jobRoleId);
  const linkState = {
    from: location,
    projectId,
    projectJobRoleSkillsProfileId: skillsProfileId,
  };

  const [setMainRoleMutation, { loading }] = useProjectJobRoleSetMainMutation({
    onError: () => {
      handleMsgType({
        type: TNotification.error,
        title: `Failed to set ${name} as main role`,
        dismissable: true,
        message: 'Something went wrong. Please try again later.',
      });
    },
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: `${name} set as main role`,
        dismissable: true,
      });
    },
    update: (cache, { data }) => {
      if (!data) return;

      const cachedProjectJobRoles = cache.readQuery<
        ProjectJobRoleFindManyQuery,
        ProjectJobRoleFindManyQueryVariables
      >({
        query: ProjectJobRoleFindManyDocument,
        variables: { id: projectId },
      });

      if (!cachedProjectJobRoles?.ProjectJobRoleFindMany?.length) return;

      const updatedProjectJobRoles =
        cachedProjectJobRoles?.ProjectJobRoleFindMany?.map(
          (projectJobRole) => ({
            ...projectJobRole,
            isMainRole: projectJobRole.id === id ? true : false,
          }),
        );

      cache.writeQuery({
        query: ProjectJobRoleFindManyDocument,
        variables: { id: projectId },
        data: {
          ProjectJobRoleFindMany: updatedProjectJobRoles,
        },
      });
    },
  });

  const handleSetMainRole = () => {
    setMainRoleMutation({
      variables: {
        id,
      },
    });
  };

  return (
    <div className={styles.container} data-testid="project-job-role-row">
      <div
        className={clsx(
          styles.info,
          isMultiMatchProject && styles.multiMatchProject,
        )}
      >
        <p className={styles.name} data-testid="project-job-role-row-name">
          {name}
        </p>
        {roleLevelLabel && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Role level</span>
            <span className={styles.metadataValue}>{roleLevelLabel}</span>
          </div>
        )}
        {jobFamilyLabel && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Job family</span>
            <span className={styles.metadataValue}>{jobFamilyLabel}</span>
          </div>
        )}
      </div>
      <div className={styles.btnsContainer}>
        {isMultiMatchProject &&
          (isMainRole ? (
            <Tag
              tagColor={TagColor.PURPLE}
              size={TagSize.SMALL}
              isPresentational
              isDismissible={false}
            >
              Main role
            </Tag>
          ) : (
            <SetMainRoleButton
              projectStatus={projectStatus}
              isEnabledProjectAction={isEnabledProjectAction}
              handleSetMainRole={handleSetMainRole}
              id={id}
              loading={loading}
            />
          ))}
        <Link
          to={jobRoleRoute}
          state={linkState}
          data-testid="edit-project-job-role-link"
        >
          <Button
            size="small"
            className={styles.btn}
            variant="secondary"
            leftIcon={<Icon icon="pencil" className={styles.icon} />}
          >
            Edit
          </Button>
        </Link>
        <RemoveProjectJobRoleButton
          id={id}
          projectId={projectId}
          name={name}
          projectStatus={projectStatus}
          isEnabledProjectAction={isEnabledProjectAction}
        />
      </div>
    </div>
  );
};

export default ProjectJobRoleRow;
